/**
 * Copyright © Smartex Canada All rights reserved.
 * See LICENSE for license details.
 */

import { removeFromCart } from "@/mutation/removeFromCart";
import { updateCart, type UpdateCartError } from "@/mutation/updateCart";
import type { CustomEventListener, SpecificEventListener, Timeout } from "@/types";
import { getComponentElement, getComponentElements } from "@/utils/dom/component";
import { getElements, replaceHtml } from "@/utils/dom/element";
import { getUenc } from "@/utils/strings";
import { setCartQuantity } from "./cart-quantity";
import { getFormKey } from "./formKey";
import { Loader } from "./loader";
import { globalMessageList } from "./global-message-list";
import { QtyInput } from "./qty-input";

export function initCart() {
    const element = getComponentElement(Cart.selector);
    if (element) {
        new Cart(element);
    }
}

export class Cart {
    static readonly selector = 'cart';

    readonly selectors = {
        cart: '#cart',
        form: '[data-cart="form"]',
        item: '[data-cart="item"]',
        loader: '[data-cart="loader"]',
        removeButton: '[data-cart="remove-item"]',
        clearButton: '[data-cart="clear"]',
        taxSummary: '[data-cart="tax-summary"]'
    };

    public element: HTMLElement;
    private form: HTMLFormElement;
    private clearButton: HTMLElement | null;
    private qtyInputs: QtyInputConfig[] = [];
    private removeButtons: ElementListenerConfig[] = [];
    private taxSummaries: ElementListenerConfig[] = [];

    private clearClickListener: SpecificEventListener<'click'>;
    private submitListener: SpecificEventListener<'submit'>;
    private qtyFocusListener: CustomEventListener;
    private qtyBlurListener: CustomEventListener;

    private timer: Timeout = 0;
    private loader: Loader;

    constructor(element: HTMLElement) {
        this.element = element;

        this.clearClickListener = () => this._clearCart();
        this.submitListener = (event) => this._onSubmit(event);
        this.qtyFocusListener = () => this._clearTimer();
        this.qtyBlurListener = () => this._maybeRestartTimer();

        this.form = this.element.querySelector(this.selectors.form)!;
        this.clearButton = this.element.querySelector(this.selectors.clearButton);

        this.loader = new Loader({
            element: this.element.querySelector<HTMLElement>(this.selectors.loader)
        });

        this.form.addEventListener('submit', this.submitListener);
        this.clearButton?.addEventListener('click', this.clearClickListener);

        this.initQtyInputs();
        this.initRemoveButtons();
        this.initTaxSummary();
    }

    /**
     * Destroy instance
     */
    public destroy(): void {
        this._clearTimer();

        this.qtyInputs.forEach(config => config.instance.destroy());
        this.qtyInputs = null as any;

        this.removeButtons.forEach(config => config.element.removeEventListener('click', config.listener));
        this.removeButtons = null as any;

        this.taxSummaries.forEach(config => config.element.removeEventListener('click', config.listener));
        this.taxSummaries = null as any;

        this.clearButton?.removeEventListener('click', this.clearClickListener);
        this.form.removeEventListener('submit', this.submitListener);

        this.loader.destroy();
        this.loader = null as any;

        this.clearButton = null as any;
        this.form = null as any;
        this.element = null as any;
    }

    /**
     * Initialize qty inputs
     */
    private initQtyInputs() {
        const elements = getComponentElements(QtyInput.selector, this.element);
        let totalQty = 0;

        elements.forEach(element => {
            const instance = new QtyInput(element);
            const changeListener = () => {
                config.pendingChange = instance.value !== config.initialValue;
                this._clearTimer();
                this._maybeRestartTimer();
            };

            const config: QtyInputConfig = {
                instance,
                initialValue: instance.value,
                pendingChange: false,
                changeListener
            };

            totalQty += instance.value;

            instance.on('updated', changeListener);
            instance.on('focus', this.qtyFocusListener);
            instance.on('blur', this.qtyBlurListener);

            this.qtyInputs.push(config);
        });

        setCartQuantity(totalQty);
    }

    /**
     * Initialize cart item remove buttons
     */
    private initRemoveButtons() {
        const buttons = getElements(this.selectors.removeButton, this.element);

        this.removeButtons = buttons.map(element => {
            const listener = () => this._removeItem(element);
            element.addEventListener('click', listener)

            return { element, listener };
        });
    }

    private initTaxSummary() {
        const elements = getElements(this.selectors.taxSummary, this.element);

        this.taxSummaries = elements.map(element => {
            const listener = () => element.classList.toggle('expanded');
            element.addEventListener('click', listener);

            return { element, listener };
        });
    }

    /**
     * Start update timer
     */
    private _startTimer() {
        this._clearTimer();
        this.timer = setTimeout(() => this._triggerSubmit(), 1000);
    }

    /**
     * Clear update timer
     */
    private _clearTimer() {
        clearTimeout(this.timer);
        this.timer = 0;
    }

    /**
     * Restart update timer if not already running and there are pending changes
     */
    private _maybeRestartTimer() {
        if (!this.timer && this.loader && !this.loader.visible
            && this.qtyInputs.some(config => config.pendingChange)
        ) {
            this._startTimer();
        }
    }

    /**
     * Request form submission
     */
    private _triggerSubmit() {
        this.form.requestSubmit
            ? this.form.requestSubmit()
            : this.form.dispatchEvent(new SubmitEvent('submit'));
    }

    /**
     * Clear focus from active element
     */
    private _clearFocus() {
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
    }

    /**
     * Handle form submit
     */
    private _onSubmit(event: SubmitEvent) {
        event.preventDefault();
        this.loader.show();

        this._clearFocus();

        const data = new FormData(this.form);
        if (!data.has('uenc')) {
            data.append('uenc', getUenc());
        }
        if (!data.has('form_key')) {
            data.append('form_key', getFormKey());
        }

        updateCart(this.form.action, data)
            .match(
                this._onCartUpdated.bind(this),
                this._onUpdateError.bind(this)
            );
    }

    /**
     * Send cart clear request
     */
    private _clearCart() {
        this._clearTimer();
        this.loader.show();
        this._clearFocus();

        const data = new FormData();
        data.append('cart[empty_cart]', '1');
        data.append('uenc', getUenc());
        data.append('form_key', getFormKey());

        updateCart(this.form.action, data)
            .match(
                this._onCartUpdated.bind(this),
                this._onUpdateError.bind(this)
            );
    }

    /**
     * Remove item from cart
     */
    private _removeItem(button: HTMLElement) {
        this._clearTimer();

        const itemId = button.getAttribute('data-item-id')
            || button.closest(this.selectors.item)?.getAttribute('data-item-id');

        if (!itemId) {
            this._maybeRestartTimer();
            return;
        }

        this.loader.show();
        this._clearFocus();

        const data = new FormData();
        data.append('item_id', itemId);
        data.append('uenc', getUenc());
        data.append('form_key', getFormKey());

        removeFromCart(data).match(
            this._onCartUpdated.bind(this),
            this._onUpdateError.bind(this)
        );
    }

    /**
     * Handle cart update success
     */
    private _onCartUpdated(result: string) {
        replaceHtml(this.selectors.cart, result);

        this.destroy();
        setCartQuantity(0);
        initCart();
        globalMessageList.getCookieMessages();
    }

    /**
     * Handle cart update error
     */
    private _onUpdateError(error: UpdateCartError) {
        this.loader.hide();

        if (error.error instanceof Error) {
            if (error.error.name !== 'AbortError') {
                console.error(error);
            }
            error.error = '';
        }

        if (!error.error) {
            error.error = 'There was an issue updating your cart.';
        }

        globalMessageList.addErrorMessage(error.error);
        window.scrollTo(0, 0);
    }
}

interface QtyInputConfig {
    instance: QtyInput
    initialValue: number
    pendingChange: boolean
    changeListener: CustomEventListener<number>
};

interface ElementListenerConfig {
    element: HTMLElement
    listener: SpecificEventListener<'click'>
}
